import React, { useEffect, useState, useRef } from 'react';
import { StyleSheet, css } from 'aphrodite';
import { motion, AnimatePresence } from 'framer-motion';
import HospitalRegister from '../../../components/hospital/forms/register.js';
import HospitalLogin from '../../../components/hospital/forms/login.js';
import HospitalForgotPassword from '../../../components/hospital/forms/forgotpassword.js';
import HospitalResetPassword from '../../../components/hospital/forms/resetpassword.js';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import {
	BG_IMAGE_URLS,
	TOASTIFY_NEWEST_ON_TOP,
} from '../../../utils/config/config.js';
import BackToHome from '../../../components/hospital/navigation/BackToHome.js';
import { APP_URLS } from '../../../../urls.js';
import LOGO from '../../../../../static/images/user/mtreat-transparentbg.png';
import { useSelector } from 'react-redux';
import { CircularProgress } from '@mui/material';
import { useHospitalAuth } from '../../../hooks/useHospitalAuth';

const HospitalAuthPage = ({ initialFormType = 'login' }) => {
	const [activeForm, setActiveForm] = useState(initialFormType);
	const navigate = useNavigate();
	const scrollContentRef = useRef(null); // Reference to the scroll container

	// Check if we need token validation for reset password
	const queryParams = new URLSearchParams(location.search);
	const hasToken =
		queryParams.has('token') && queryParams.get('token').length > 10;
	const hasUid = queryParams.has('uid') && queryParams.get('uid').length > 0;
	const hasValidResetParams = hasToken && hasUid;

	// Get authentication state with loading indicator
	const { isAuthenticated, isLoading } = useHospitalAuth(false, false, toast);

	// Check for redirects after login
	const { from } = location.state || {
		from: { pathname: APP_URLS.hospitalDashboard },
	};

	useEffect(() => {
		if (scrollContentRef.current) {
			setTimeout(() => {
				scrollContentRef.current.scrollTo({
					top: 0,
					behavior: 'smooth',
				});
			}, 100);
		}
	}, [activeForm]);

	// Redirect authenticated users
	useEffect(() => {
		if (isAuthenticated && !isLoading) {
			navigate(from);
		}
	}, [isAuthenticated, isLoading, navigate, from]);

	// Handle form navigation
	const handleFormChange = (formType) => {
		setActiveForm(formType);

		// Update URL path based on form type
		switch (formType) {
			case 'login':
				navigate(APP_URLS.hospitalLogin);
				break;
			case 'register':
				navigate(APP_URLS.hospitalRegister);
				break;
			case 'forgotPassword':
				navigate(APP_URLS.hospitalForgotPassword);
				break;
			case 'resetPassword':
				// Keep existing token parameter if switching to reset password
				navigate(`${APP_URLS.hospitalResetPassword}${location.search}`);
				break;
			default:
				navigate(APP_URLS.hospitalLogin);
		}
	};

	// Form specific titles and toggle text
	const getFormTitle = () => {
		switch (activeForm) {
			case 'login':
				return 'Welcome back';
			case 'register':
				return 'Create a new account';
			case 'forgotPassword':
				return 'Reset your password';
			case 'resetPassword':
				return 'Set a new password';
			default:
				return 'Welcome';
		}
	};

	// Show loading state while checking authentication
	if (isLoading) {
		return (
			<div className={css(styles.loadingContainer)}>
				<CircularProgress size={48} />
				<p className={css(styles.loadingText)}>Loading...</p>
			</div>
		);
	}

	return (
		<div className={css(styles.container)}>
			<div className={css(styles.contentWrapper)}>
				<div className={css(styles.imageSection)}>
					<img
						src={
							activeForm === 'resetPassword' || activeForm === 'forgotPassword'
								? BG_IMAGE_URLS.resetPasswordPage
								: BG_IMAGE_URLS.hospitalauthPage || '/placeholder.svg'
						}
						alt="Medical illustration"
						className={css(styles.image)}
					/>
				</div>
				<motion.div
					className={css(styles.formContainer)}
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.5 }}
				>
					<div ref={scrollContentRef} className={css(styles.scrollContent)}>
						<motion.div
							initial={{ opacity: 0, scale: 0.8 }}
							animate={{ opacity: 1, scale: 1 }}
							transition={{ duration: 0.5 }}
							className={css(styles.logoContainer)}
						>
							<img
								src={LOGO || '/placeholder.svg'}
								alt="Company logo"
								className={css(styles.logo)}
							/>
							<div className={css(styles.divider)} />
							<motion.div
								initial={{ opacity: 0, x: -20 }}
								animate={{ opacity: 1, x: 0 }}
								transition={{ duration: 0.5, delay: 0.2 }}
								className={css(styles.companyName)}
							>
								M-Treat
							</motion.div>
						</motion.div>

						<h1 className={css(styles.title)}>{getFormTitle()}</h1>
						<BackToHome />

						<AnimatePresence mode="wait">
							<motion.div
								key={activeForm}
								initial={{ opacity: 0, x: -20 }}
								animate={{ opacity: 1, x: 0 }}
								exit={{ opacity: 0, x: 20 }}
								transition={{ duration: 0.3 }}
							>
								{activeForm === 'login' && (
									<HospitalLogin
										toast={toast}
										onForgotPassword={() => handleFormChange('forgotPassword')}
									/>
								)}
								{activeForm === 'register' && (
									<HospitalRegister toast={toast} />
								)}
								{activeForm === 'forgotPassword' && (
									<HospitalForgotPassword
										toast={toast}
										onBackToLogin={() => handleFormChange('login')}
									/>
								)}
								{activeForm === 'resetPassword' && (
									<HospitalResetPassword
										toast={toast}
										uid={queryParams.get('uid')}
										token={queryParams.get('token')}
										hasValidResetParams={hasValidResetParams}
										onSuccess={() => handleFormChange('login')}
									/>
								)}
							</motion.div>
						</AnimatePresence>

						{/* Toggle links between login and register */}
						{(activeForm === 'login' || activeForm === 'register') && (
							<p className={css(styles.toggleText)}>
								{activeForm === 'login'
									? "Don't have an account? "
									: 'Already have an account? '}
								<span
									className={css(styles.toggleLink)}
									onClick={() =>
										handleFormChange(
											activeForm === 'login' ? 'register' : 'login',
										)
									}
								>
									{activeForm === 'login' ? 'Register' : 'Login'}
								</span>
							</p>
						)}

						{/* Back to login from forgot password */}
						{activeForm === 'forgotPassword' && (
							<p className={css(styles.toggleText)}>
								Remember your password?{' '}
								<span
									className={css(styles.toggleLink)}
									onClick={() => handleFormChange('login')}
								>
									Back to login
								</span>
							</p>
						)}
					</div>
				</motion.div>
			</div>
			<ToastContainer newestOnTop={TOASTIFY_NEWEST_ON_TOP} />
		</div>
	);
};

const styles = StyleSheet.create({
	loadingContainer: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
		alignItems: 'center',
		height: '100vh',
		width: '100vw',
		backgroundColor: '#f0f4f8',
	},
	loadingText: {
		marginTop: '1rem',
		fontSize: '1.25rem',
		color: '#2783F7',
	},
	container: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		width: '100vw',
		backgroundColor: '#f0f4f8',
		fontFamily: "'Roboto', sans-serif",
		padding: '1rem',
		boxSizing: 'border-box',
		'@media (max-width: 768px)': {
			padding: '0.5rem',
		},
		padding: '1rem',
		'@media (min-width: 1307px)': {
			height: '100vh',
		},
	},
	contentWrapper: {
		display: 'flex',
		maxWidth: '1200px',
		width: '100%',
		height: '100%',
		backgroundColor: 'white',
		borderRadius: '12px',
		overflow: 'hidden',
		boxShadow: '0 10px 25px rgba(0, 0, 0, 0.05)',
		'@media (max-width: 1307px)': {
			flexDirection: 'column',
			minHeight: 'calc(100vh - 1rem)',
		},
	},
	imageSection: {
		flex: 1,
		display: 'none',
		'@media (min-width: 1307px)': {
			display: 'block',
		},
	},
	image: {
		width: '100%',
		height: '100%',
		objectFit: 'cover',
	},
	formContainer: {
		flex: 1,
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'space-between',
		padding: '2rem',
		maxWidth: '500px',
		margin: '0 auto',
		'@media (min-width: 1307px)': {
			overflowY: 'auto',
			padding: '2rem',
		},
	},
	scrollContent: {
		flex: 1,
		display: 'flex',
		flexDirection: 'column',
		WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
	},
	logoContainer: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		marginBottom: '2rem',
		'@media (max-width: 768px)': {
			marginBottom: '1.5rem',
		},
	},
	logo: {
		width: '55px',
		height: 'auto',
	},
	divider: {
		height: '40px',
		width: '1px',
		backgroundColor: '#e2e8f0',
		margin: '0 1rem',
	},
	companyName: {
		fontSize: '1.8rem',
		fontWeight: '700',
		color: '#2783F7',
		letterSpacing: '0.5px',
	},
	title: {
		color: 'black',
		textAlign: 'center',
		marginTop: '0.5rem',
		marginBottom: '1.5rem',
		fontSize: '2rem',
		fontWeight: '700',
		'@media (max-width: 480px)': {
			fontSize: '1.5rem',
			marginBottom: '1rem',
		},
	},
	toggleText: {
		textAlign: 'center',
		marginTop: '1.5rem',
		marginBottom: '1rem',
		color: '#4a5568',
	},
	toggleLink: {
		color: '#2783F7',
		cursor: 'pointer',
		fontWeight: '600',
		':hover': {
			textDecoration: 'underline',
		},
	},
});

export default HospitalAuthPage;
